import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { BASE_API_URL } from "store/Me";
import { AdminTable } from "components/shared/AdminTable";
import { AdminTablePagination } from "components/shared/AdminTablePagination";
import GuestOnboardingModal from "components/shared/GuestOnboardingModal";
import useDebounce from "utils/useDebounce";
import getGuestDisplayName from "utils/getGuestDisplayName";
import { Button } from "components/ui/button";
import { Search } from "lucide-react";

const SITE_GUESTS_QUERY_KEY = "site-guests-list";
const DEFAULT_PAGE_SIZE = 50;

type GuestRecordResponse = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  validFrom?: string;
  validTo?: string;
  deletedTimestamp?: string;
  guestTypes?: string[];
};

async function getSiteGuestList(
  siteId: string,
  search: string,
  skip: number,
  take: number,
): Promise<GuestRecordResponse[]> {
  const params = new URLSearchParams();
  if (search) params.append("search", search);
  params.append("skip", skip.toString());
  params.append("take", take.toString());
  const res = await axios.get<GuestRecordResponse[]>(
    `${BASE_API_URL}/api/Sites/${siteId}/guests/list?${params.toString()}`,
  );
  return res.data ?? [];
}

function useGetSiteGuestList(
  siteId: string,
  search: string,
  skip: number,
  take: number,
) {
  return useQuery({
    queryKey: [SITE_GUESTS_QUERY_KEY, siteId, search, skip, take],
    queryFn: () => getSiteGuestList(siteId, search, skip, take),
  });
}

export default function SiteGuests({ siteId }: { siteId: string }) {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);

  const { data, isLoading } = useGetSiteGuestList(
    siteId,
    debouncedSearch,
    page * pageSize,
    pageSize,
  );
  const guests = data ?? [];

  if (isLoading) {
    return (
      <div className="flex min-h-80 items-center justify-center rounded-lg border border-slate-200 bg-white text-sm text-slate-500 shadow-sm">
        Loading guests...
      </div>
    );
  }

  const handleModalClose = (submitted?: boolean) => {
    setModalOpen(false);
    if (submitted) {
      queryClient.invalidateQueries({ queryKey: [SITE_GUESTS_QUERY_KEY, siteId] });
    }
  };

  return (
    <>
      <AdminTable
        aria-label="Site guests"
        toolbar={
          <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
            <label className="relative block w-full sm:w-72">
              <span className="sr-only">Search guests</span>
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400"
              />
              <input
                className="h-10 w-full rounded-md border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                placeholder="Search by name, email, unit..."
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
              />
            </label>
            <Button
              onClick={() => setModalOpen(true)}
              size="sm"
              type="button"
            >
              Onboard Guest
            </Button>
          </div>
        }
        footer={
          <AdminTablePagination
            currentPage={page}
            pageSize={pageSize}
            visibleRowCount={guests.length}
            isLoading={isLoading}
            onNext={() => setPage((currentPage) => currentPage + 1)}
            onPrevious={() => setPage((currentPage) => Math.max(0, currentPage - 1))}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
            disablePrevious={page === 0}
            disableNext={guests.length < pageSize}
          />
        }
      >
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Email</th>
            <th scope="col">Types</th>
            <th scope="col">Valid From</th>
            <th scope="col">Valid To</th>
            <th scope="col">Deleted</th>
          </tr>
        </thead>
        <tbody>
          {guests.map((guest) => (
            <tr key={guest.id}>
              <td>
                <Link to={`/admin/guests/${encodeURIComponent(guest.id ?? "")}`}>
                  {getGuestDisplayName(guest.firstName, guest.lastName)}
                </Link>
                <div style={{ paddingTop: 4, fontSize: 12, color: "gray" }}>
                  {guest.id}
                </div>
              </td>
              <td>{guest.email}</td>
              <td>{(guest.guestTypes || []).join(", ") || "—"}</td>
              <td>{guest.validFrom ? new Date(guest.validFrom).toLocaleString() : "—"}</td>
              <td>{guest.validTo ? new Date(guest.validTo).toLocaleString() : "—"}</td>
              <td>{guest.deletedTimestamp ? "Yes" : "No"}</td>
            </tr>
          ))}
          {guests.length === 0 && (
            <tr>
              <td colSpan={6} className="py-16 text-center text-sm text-slate-500">
                No guests found for this site.
              </td>
            </tr>
          )}
        </tbody>
      </AdminTable>
      <GuestOnboardingModal open={modalOpen} siteId={siteId} onClose={handleModalClose} />
    </>
  );
}
